<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <span>{{ $t('admin_achievement_coupon_progress') }}: <strong>{{ meta.used_coupons }} / {{ meta.total_coupons }}</strong></span>
        <b-progress :max="meta.total_coupons" :value="meta.used_coupons" height="2rem" class="mt-1"/>
      </div>
    </div>
    <div class="row justify-content-end">
      <div class="col-12 col-xl-4">
          <b-form-group :label="$dt('discount_code_import', 'Importēt CSV', 'admin')">
            <b-input-group>
              <b-form-file v-model="uploadedFile" accept="text/csv" />
              <b-input-group-append>
                <b-button :disabled="!uploadedFile" variant="primary" @click="importCodes">
                  <b-spinner v-if="spinners.importing" small variant="light" />
                  <span v-else>{{ $dt('import', 'Importēt') }}</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback v-if="errors['file']" :state="false">
              {{ errors['file'][0] }}
            </b-form-invalid-feedback>
          </b-form-group>
      </div>
      
      <div class="col-12 col-xl-4">
          <b-form-group :label="$dt('discount_code_add', 'Pievienot kodu', 'admin')">
            <b-input-group>
              <b-form-input v-model="newCode" size="sm" />
              <b-input-group-append>
                <b-button :disabled="!newCode" variant="success" size="sm" @click="saveCode">
                  <b-spinner v-if="spinners.importing || spinners.submitting" variant="light" />
                  <span v-else>{{ $dt('btn.add', 'Pievienot') }}</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback v-if="errors['code']" :state="false">
              {{ errors['code'][0] }}
            </b-form-invalid-feedback>
          </b-form-group>
      </div>
    </div>
    
    <div class="row">
      <div class="col-12 discount-codes-table">
        <b-table 
          :items="codes"
          :fields="tableFields"
          striped
          responsive="sm">
          <template v-slot:is_claimed="{ item }">
            <b-form-checkbox :value="item.claimed_user !== null" />
          </template>
          <template v-slot:user_name="{ item }">
            <span v-if="item.claimed_user !== null">{{ item.claimed_user.full_name }}</span>
          </template>
          <template v-slot:user_email="{ item }">
            <span v-if="item.claimed_user !== null">{{ item.claimed_user.email }}</span>
          </template>
          <template v-slot:actions="{ item }">
            <div class="text-center actions d-flex">
              <b-button v-if="!item.claimed_user" class="rounded-circular" @click="deleteCode(item.id)" >
                <i class="fa fa-trash text-danger" />
              </b-button>
            </div>
          </template>
        </b-table>

        <div v-if="spinners.contentIsLoading" class="col-12 text-center discount-codes-table-loading">
          <b-spinner type="grow" variant="primary" class="discount-codes-table-spinner"/>
        </div>
      </div>
    </div>
    <Pagination :data="meta" :limit="5" @pagination-change-page="(page) => { this.page = page }"/>
  </div>
</template>

<script>
import Services from '@/services';
import Pagination from 'laravel-vue-pagination';

export default {
  name: 'DiscountCodeList',
  components: { Pagination },
  props: {
    achievement_id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      spinners: {
        contentIsLoading: true,
        submitting: false,
        importing: false,
      },
      errors: {},
      newCode: '',
      uploadedFile: null,
      codes: [],
      meta: {
        total_coupons: 0,
        used_coupons: 0,
      },
      tableFields: [
        { key: 'id', label: 'Id' },
        { key: 'code', label: this.$dt('discount_code', 'Kods', 'admin') },
        { key: 'user_name', label: this.$dt('user_fullname', 'Vārds/Uzvārds') },
        { key: 'user_email', label: this.$dt('user_email', 'E-pasts') },
        { key: 'actions', label: this.$dt('discount_code_actions', 'Darbības', 'admin') },
      ],
    }
  },
  watch: {
    page () {
      this.getData();
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.spinners.contentIsLoading = true;
      Services.admin.achievementDiscountCodeList(this.achievement_id, this.page)
        .then(({ data }) => {
          this.codes = data.data;
          this.meta = data.meta;

          if (this.meta.current_page > this.meta.last_page) {
            this.page = this.meta.last_page;
          }
        })
        .catch(() => {
          this.alertError();
        })
        .finally(() => {
          this.spinners.contentIsLoading = false;
        });
    },
    saveCode() {
      this.spinners.submitting = true;
      this.errors.code = [];
      Services.admin.achievementDiscountCodeCreate(this.achievement_id, this.newCode)
        .then(() => {
          this.alertSuccess()
          this.newCode = '';
          this.getData();
        })
        .catch((error) => {
          this.errors = error.data.data.errors || {};
          this.alertError();
        })
        .finally(() => {
          this.spinners.submitting = false;
        });
    },
    deleteCode(id) {
      this.spinners.submitting = true;
      Services.admin.achievementDiscountCodeDelete(this.achievement_id, id)
        .then(() => {
          this.alertSuccess()
          this.getData();
        })
        .catch(() => {
          this.alertError();
        })
        .finally(() => {
          this.spinners.submitting = false;
        });
    },
    importCodes() {
      this.spinners.importing = true;
      this.errors.file = [];

      const data = new FormData();
      data.append('file', this.uploadedFile);

      Services.admin.achievementDiscountCodeImport(this.achievement_id, data)
        .then(() => {
          this.alertSuccess()
          this.uploadedFile = null;
          this.getData();
        })
        .catch((error) => {
          this.errors = error.data.data.errors || {};
          this.alertError();
          this.uploadedFile = null;
        })
        .finally(() => {
          this.spinners.importing = false;
        });
    },
  },
}
</script>

<style lang="scss" scoped>
.actions button {
  background: none;
  border: none;
  font-size: 1.5rem;
}

.discount-codes-table {
  position: relative;

  &-loading {
    position: absolute;
    height: 100%;
    width: 100%;
    top:0;
    left:0;
    background-color: rgba(255, 255, 255, 0.6);
  }

  &-spinner {
    position: absolute;
    top: 5%;
  }
}
</style>